import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Lock, Unlock } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { GateBadge } from './GateBadge'
import { IndicatorRow } from './IndicatorRow'
import type { CategoryAccordionProps, IndicadorDados } from './types'
import type { FieldPermissions } from '@/lib/bonificacao/permissions'

function getProgressColor(media: number): string {
  if (media >= 100) return 'bg-brand-primary'
  if (media >= 70) return 'bg-warning'
  return 'bg-brand-gray/50'
}

function getProgressTextColor(media: number): string {
  if (media >= 100) return 'text-brand-primary'
  if (media >= 70) return 'text-warning'
  return 'text-brand-gray'
}

export function CategoryAccordion({
  categoria,
  dados,
  bonusResult,
  readOnly,
  children,
  assessmentId,
  assessmentStatus,
  permissions,
  onIndicatorUpdate,
  savingIndicatorId,
  publicado,
  isRH,
  onTogglePublish,
}: CategoryAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  // Trigger progress bar animation on first render
  useEffect(() => {
    const timer = setTimeout(() => setHasAnimated(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const isGateBlocked = bonusResult?.gateBlocked ?? false
  const hasGate = !!categoria.gateCat

  // Render IndicatorRows when no children provided and wiring props are available
  const renderIndicators = !children && assessmentStatus && permissions && onIndicatorUpdate

  return (
    <div
      className="card"
      data-testid={`category-accordion-${categoria.id}`}
    >
      {/* Header — always visible */}
      <div
        className="cursor-pointer"
        data-testid={`category-accordion-header-${categoria.id}`}
        onClick={() => setIsExpanded(prev => !prev)}
      >
        {/* Top row: weight badge + name + gate + chevron */}
        <div className="flex items-center gap-3">
          <Badge
            variant="info"
            data-testid={`weight-badge-${categoria.id}`}
          >
            {categoria.peso}%
          </Badge>
          <span className="font-display font-semibold text-white flex-1">
            {categoria.nome}
          </span>
          {hasGate && (
            <GateBadge
              blocked={isGateBlocked}
              gateInfo={
                bonusResult
                  ? {
                      ref: categoria.gateCat!,
                      min: categoria.gateMin ?? 0,
                      atual: bonusResult.media,
                    }
                  : undefined
              }
            />
          )}
          {/* GAP 4: Publish toggle — visible only for RH roles */}
          {isRH && onTogglePublish && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onTogglePublish(categoria.id, !publicado)
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                publicado
                  ? 'bg-green-500/15 text-green-400 border border-green-500/20 hover:bg-green-500/25'
                  : 'bg-brand-dark-lighter text-brand-gray border border-brand-gray/20 hover:text-white hover:border-brand-gray/40'
              }`}
              data-testid={`publish-toggle-${categoria.id}`}
            >
              {publicado ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              {publicado ? 'Público' : 'Tornar Público'}
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-brand-gray motion-safe:transition-transform motion-safe:duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>

        {/* Meta info line */}
        <div className="flex items-center gap-2 mt-2 text-sm text-brand-gray">
          <span>{categoria.indicadores.length} indicadores</span>
          <span className="opacity-40">·</span>
          <span>{categoria.salarios} sal.</span>
          <span className="opacity-40">·</span>
          <span>Teto {categoria.teto}%</span>
        </div>

        {/* Progress bar + salary earned */}
        <div className="flex items-center gap-4 mt-3">
          {isGateBlocked ? (
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm text-brand-gray">
                <Lock className="w-3.5 h-3.5 text-error/70" />
                <span>Gate não atingido — pré-requisito abaixo do mínimo</span>
              </div>
            </div>
          ) : (
            <div className="flex-1">
              <div
                className="relative h-2 w-full overflow-hidden rounded-full bg-white/10"
                data-testid={`progress-bar-${categoria.id}`}
              >
                <div
                  className={`h-full motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${getProgressColor(bonusResult?.media ?? 0)}`}
                  style={{ width: hasAnimated ? `${Math.min(bonusResult?.media ?? 0, 100)}%` : '0%' }}
                />
              </div>
              <span className={`text-xs mt-1 inline-block ${getProgressTextColor(bonusResult?.media ?? 0)}`}>
                {bonusResult?.media != null ? `${bonusResult.media.toFixed(0)}%` : '—'}
              </span>
            </div>
          )}
          <span className="text-sm text-brand-primary font-semibold whitespace-nowrap">
            {bonusResult?.bonusSalarios != null
              ? `${bonusResult.bonusSalarios.toFixed(2)} sal.`
              : '— sal.'}
          </span>
        </div>
      </div>

      {/* Expanded body */}
      <div
        ref={contentRef}
        className="overflow-hidden motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-in-out"
        style={{
          maxHeight: isExpanded ? `${(contentRef.current?.scrollHeight ?? 0) + 200}px` : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div
          className="mt-3 pt-3 border-t border-brand-gray/10 space-y-3"
          data-testid={`category-accordion-body-${categoria.id}`}
        >
          {renderIndicators
            ? categoria.indicadores.map((indicador) => {
                const indicadorDados = dados[indicador.id]
                const isAutoCalc = !!(indicadorDados as unknown as Record<string, unknown> | undefined)?._autoCalculated
                  || indicador.fonte === 'proa-auto'
                  || indicador.fonte === 'performance-data'

                return (
                  <IndicatorRow
                    key={indicador.id}
                    indicador={categoria.binario ? { ...indicador, binario: true } : indicador}
                    dados={indicadorDados}
                    assessmentId={assessmentId ?? ''}
                    assessmentStatus={assessmentStatus}
                    permissions={permissions}
                    onUpdate={onIndicatorUpdate}
                    isSaving={savingIndicatorId === indicador.id}
                    isAutoCalculated={isAutoCalc}
                    autoCalcHelperText={
                      isAutoCalc
                        ? (indicadorDados?._helperText || (indicadorDados?.valor != null ? `Valor calculado: ${indicadorDados.valor}` : undefined))
                        : undefined
                    }
                  />
                )
              })
            : children}
        </div>
      </div>
    </div>
  )
}
